import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import AddressFields, { isAddressComplete } from "@/components/app/AddressFields";
import { toast } from "sonner";

const EMPTY_ADDR = { cep: "", street: "", number: "", complement: "", neighborhood: "", city: "", state: "" };

export default function Register() {
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", document: "", password: "" });
  const [addr, setAddr] = useState(EMPTY_ADDR);
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const maskDocument = (v) => {
    const d = (v || "").replace(/\D/g, "").slice(0, 14);
    if (d.length <= 11) {
      return d
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }
    return d
      .replace(/(\d{2})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1/$2")
      .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
  };

  const updDocument = (e) => setForm({ ...form, document: maskDocument(e.target.value) });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres.");
      return;
    }
    const digits = form.document.replace(/\D/g, "");
    if (digits.length !== 11 && digits.length !== 14) {
      toast.error("Informe um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.");
      return;
    }
    if (!isAddressComplete(addr)) {
      toast.error("Endereço é obrigatório (CEP, rua, cidade e UF).");
      return;
    }
    setLoading(true);
    try {
      await register({
        ...form, document: digits,
        address: {
          cep: addr.cep, street: addr.street, number: addr.number || null,
          complement: addr.complement || null, neighborhood: addr.neighborhood || null,
          city: addr.city, state: addr.state,
        },
      });
      toast.success("Conta criada com sucesso!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <Card className="w-full max-w-md border-slate-200 shadow-sm">
        <CardContent className="p-8">
          <div className="flex items-center justify-center mb-6">
            <img src="/logo-clima-branca.png" alt="Clima Branca Franquias" className="h-14" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Criar conta</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">
            Cadastre-se para abrir chamados de atendimento
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Nome completo</Label>
              <Input id="name" required value={form.name} onChange={upd("name")}
                placeholder="Seu nome" data-testid="register-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">E-mail</Label>
              <Input id="email" type="email" required value={form.email} onChange={upd("email")}
                placeholder="seu@email.com" data-testid="register-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Telefone / WhatsApp</Label>
              <Input id="phone" required value={form.phone} onChange={upd("phone")}
                placeholder="(21) 99999-9999" data-testid="register-phone-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="document">CPF ou CNPJ</Label>
              <Input id="document" required value={form.document} onChange={updDocument}
                placeholder="000.000.000-00" data-testid="register-document-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Senha</Label>
              <Input id="password" type="password" required value={form.password} onChange={upd("password")}
                placeholder="Mínimo 6 caracteres" data-testid="register-password-input" />
            </div>
            <div className="pt-1">
              <Label className="text-sm font-semibold text-slate-700">Endereço</Label>
              <p className="text-xs text-slate-400 mb-2">Usado para direcionar o atendimento à franquia da sua região.</p>
              <AddressFields value={addr} onChange={setAddr} idPrefix="register-addr" />
            </div>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700"
              disabled={loading} data-testid="register-submit-button">
              {loading ? "Criando..." : "Criar conta"}
            </Button>
          </form>

          <p className="text-sm text-slate-500 text-center mt-6">
            Já tem conta?{" "}
            <Link to="/" className="text-blue-600 font-medium hover:underline" data-testid="go-to-login-link">
              Entrar
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
